/**
 * Audit trail (super_admin). The hash-chained, append-only log with a live integrity
 * verify badge and CSV/JSON export. Filter by action / entity.
 */
import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, Download, RefreshCw, Search } from 'lucide-react';
import { api, getAccessToken } from '../lib/apiClient.js';
import { useToast } from '../components/feedback/ToastProvider.jsx';
import { formatDate } from '../features/cases/caseMeta.js';
import styles from './Audit.module.css';

export default function Audit() {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [state, setState] = useState({ status: 'loading', items: [], nextCursor: null, hasMore: false });
  const [verify, setVerify] = useState(null);

  const load = useCallback(
    async (append = false) => {
      setState((s) => ({ ...s, status: append ? s.status : 'loading' }));
      try {
        const params = new URLSearchParams({ limit: '40' });
        if (q) params.set('action', q);
        if (append && state.nextCursor) params.set('cursor', state.nextCursor);
        const env = await api.getEnvelope(`/audit?${params}`);
        setState((s) => ({
          status: 'ready',
          items: append ? [...s.items, ...env.data] : env.data,
          nextCursor: env.meta.page.nextCursor,
          hasMore: env.meta.page.hasMore,
        }));
      } catch (err) {
        setState({ status: 'error', items: [], error: err.message });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [q],
  );

  useEffect(() => {
    const t = setTimeout(() => load(false), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const runVerify = async () => {
    try {
      const res = await api.get('/audit/verify');
      setVerify(res);
      toast[res.ok ? 'success' : 'error'](
        res.ok ? `Chain intact — ${res.verifiedEntries} entries.` : `Broken at #${res.brokenAtSeq}!`,
      );
    } catch (err) {
      toast.error(err.message);
    }
  };

  const exportLog = (format) => {
    // Stream the server-side export (authoritative, includes hashes).
    const base = import.meta.env.VITE_API_BASE_URL || '/api';
    fetch(`${base}/audit/export?format=${format}`, {
      headers: { Authorization: `Bearer ${getAccessToken()}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `audit-log.${format}`;
        a.click();
        toast.success(`Audit log exported (${format.toUpperCase()}).`);
      })
      .catch(() => toast.error('Export failed.'));
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="eyebrow">Reporting</p>
          <h1 className={styles.h1}>Audit trail</h1>
          <p className={styles.lede}>Append-only, SHA-256 hash-chained. Tamper-evident on export.</p>
        </div>
        <div className={styles.headActions}>
          <button className={styles.verifyBtn} onClick={runVerify}>
            <ShieldCheck size={16} /> Verify integrity
          </button>
          <button className={styles.ghost} onClick={() => exportLog('csv')}>
            <Download size={15} /> Export CSV
          </button>
          <button className={styles.ghost} onClick={() => exportLog('json')}>
            <Download size={15} /> Export JSON
          </button>
        </div>
      </header>

      {verify && (
        <div className={verify.ok ? styles.ok : styles.bad}>
          {verify.ok ? <ShieldCheck size={18} /> : <ShieldAlert size={18} />}
          {verify.ok
            ? `Hash chain intact — ${verify.verifiedEntries} entries verified end to end.`
            : `Tampering detected at entry #${verify.brokenAtSeq}.`}
        </div>
      )}

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={16} />
          <input placeholder="Filter by action (e.g. case.intake)…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button className={styles.ghost} onClick={() => load(false)}>
          <RefreshCw size={15} />
        </button>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Action</th>
              <th>Actor</th>
              <th>Entity</th>
              <th>When</th>
              <th>Hash</th>
            </tr>
          </thead>
          <tbody>
            {state.status === 'loading' &&
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={6}>
                    <div className={styles.skeleton} />
                  </td>
                </tr>
              ))}
            {state.status === 'ready' &&
              state.items.map((a) => (
                <tr key={a.id || a.seq}>
                  <td className="mono">{a.seq}</td>
                  <td>
                    <code className={styles.action}>{a.action}</code>
                  </td>
                  <td>{a.actor?.name || 'system'}</td>
                  <td className={styles.sub}>
                    {a.entityType ? `${a.entityType}` : '—'}
                  </td>
                  <td className={styles.sub}>{formatDate(a.at)}</td>
                  <td>
                    <code className={styles.hash} title={a.hash}>
                      {a.hash?.slice(0, 10)}…
                    </code>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {state.hasMore && state.status === 'ready' && (
          <button className={styles.loadMore} onClick={() => load(true)}>
            Load more
          </button>
        )}
      </div>
    </div>
  );
}
